import { useQuery, type QueryClient } from '@tanstack/react-query'
import { frameShell } from '../shell/shellOrigin'
import { getArtifactsWhere, requestArtifactTicket, type SharedArtifact } from '../transport/relay'
import { artifactsHostOf, contentFrameAttrs, contentFrameGate, sharedViewGate } from './everywhere'

/**
 * THE ARTIFACTS' OWN HOST, AS THIS PAGE KNOWS IT — owner, 2026-10-05: artifact pages move
 * to a hostname of their own, and a friend's shared artifact opens in the App's main pane
 * with the built-in viewer.
 *
 * everywhere.ts holds the RULES, as pure functions. This file is where they meet the three
 * things only a running page has: what the cloud said about where artifacts are served,
 * this page's own origin, and the shell around it. Whoever has to decide "frame it or not"
 * comes through here — so the row that opens the viewer and the viewer that checks again
 * are reading one answer, not two.
 *
 * WHERE ARTIFACTS ARE SERVED is one cached query. The More panel asks it (a row can only
 * be tapped there); whatever below decides about a FRAME only reads it, at the moment of
 * deciding, and nothing subscribes a frame to it. A frame's address is settled once per
 * open: an answer that arrives or changes afterwards must never navigate a page somebody
 * is using. (The one other asker is the boot-time sweep at the bottom of this file, which
 * runs with no panel behind it.)
 *
 * NOT KNOWN IS THE ORDINARY CASE, AND IT MEANS "AS BEFORE". The request failing in ANY way
 * counts — a cloud that predates the route (404), an answer that is not JSON, the network —
 * and so does an answer that could not be refreshed: only a request that succeeded says
 * where artifacts are. Not knowing costs nothing but the old path, which still works.
 */
const WHERE = {
  queryKey: ['artifacts-where'],
  queryFn: getArtifactsWhere,
  /*
   * Where artifacts are served changes once per environment, at its cutover. Five minutes
   * keeps an open More panel from asking on every return to the window, and a cutover is
   * still picked up within minutes of the next one. Being behind meanwhile is harmless:
   * it is the old path, which keeps working on both sides of a cutover.
   */
  staleTime: 5 * 60_000,
  /*
   * ONE REQUEST, NO LADDER. The app's default retries once, after a pause, before it will
   * call a failure a failure — and an own artifact opened in that pause would wait it out.
   * It may wait for the request itself and no longer; the next visit to More asks again.
   */
  retry: false,
  /*
   * KEPT FOR THE LIFE OF THE PAGE. The viewer outlives the panel that asked: on a wide
   * screen the rail can go back to Agents with an artifact still open beside it, and five
   * minutes later the cache would have dropped an answer nobody was subscribed to — so
   * the viewer's next Reload would find "not known" and refuse a friend's page it had
   * been showing all along.
   */
  gcTime: Infinity,
} as const

/** Mounted by the More panel: asks where artifacts are served, and keeps the answer warm. */
export function useArtifactsWhere(): void {
  useQuery(WHERE)
}

type Where = Awaited<ReturnType<typeof getArtifactsWhere>>

/** `<content host>/artifacts` as the cloud last SUCCESSFULLY stated it, else null. */
function contentBaseNow(qc: QueryClient): string | null {
  const st = qc.getQueryState<Where>(WHERE.queryKey)
  if (st?.status !== 'success') return null
  const base = st.data?.content_base
  return typeof base === 'string' && base ? base : null
}

/** The very first answer is on its way: nothing is known yet, and something soon will be. */
function firstAnswerArriving(qc: QueryClient): boolean {
  const st = qc.getQueryState<Where>(WHERE.queryKey)
  return st?.status === 'pending' && st.fetchStatus === 'fetching'
}

/**
 * The content base once the request ALREADY in flight has answered. It joins that request
 * and starts none: with nothing in flight this resolves at once with what is known.
 */
async function contentBaseSettled(qc: QueryClient): Promise<string | null> {
  if (firstAnswerArriving(qc)) {
    try {
      await qc.fetchQuery(WHERE)
    } catch {
      /* It failed, which is an answer too: not known. */
    }
  }
  return contentBaseNow(qc)
}

/** A frame on the artifacts' host, ready to render: what to load, and what it may do. */
export type ContentFrame = { src: string; sandbox: string; allow: string }

/**
 * A FRIEND'S ARTIFACT, HERE AND NOW: the frame to render, or why there is none.
 *
 * Asked twice per open, on purpose — by the row, to choose between the viewer and its
 * fallbacks, and again by the viewer when it mounts, which does not take the row's word
 * for it. Synchronous, because the row has to decide inside its own click.
 */
export function sharedFrameHere(
  qc: QueryClient,
  artifact: Pick<SharedArtifact, 'hash' | 'frame_url'>,
): ({ ok: true } & ContentFrame) | { ok: false; why: string } {
  const shell = frameShell()
  const gate = sharedViewGate({
    frameUrl: artifact.frame_url,
    hash: artifact.hash,
    contentBase: contentBaseNow(qc),
    appOrigin: window.location.origin,
    shell,
  })
  return gate.ok ? { ok: true, src: gate.src, ...contentFrameAttrs('shared', shell) } : gate
}

/**
 * Does opening one of this account's OWN artifacts have anything to ask the cloud first?
 *
 * False is the ordinary answer — artifacts are served on the App's own host, or nobody
 * could say otherwise — and then the viewer frames the share link at once, exactly as it
 * always has. True only when there is a host to get a ticket for, or the first word on
 * whether there is one is still in flight.
 */
export function ownFrameNeedsCloud(qc: QueryClient): boolean {
  return firstAnswerArriving(qc) || artifactsHostOf(contentBaseNow(qc), window.location.origin) !== null
}

/**
 * The frame for one of this account's OWN artifacts where artifacts have a host of their
 * own — or null, which means "there is no such host: frame the share link, as before".
 *
 * WITH A HOST THERE IS ALWAYS A FRAME, AND IT ALWAYS HAS THAT HOST'S ATTRIBUTES. The
 * ticket address when the cloud mints one that passes the gate. Otherwise — a ticket the
 * cloud would not mint, an answer that does not pass the gate, a request the network
 * dropped — the share link (`link`), which the relay sends on to the new host by itself:
 * the address differs, where the page ENDS UP does not, so what it may do must not
 * either. Framed with the attributes from before, that page would be on the artifacts'
 * host with nothing held back — inside an iOS app that does not guard its bridge, with
 * the dialogs that are a command channel there (found in review, 2026-10-05).
 *
 * NEITHER IS A FAILURE THE PERSON SEES: the artifact opens either way.
 */
export async function ownFrameHere(qc: QueryClient, hash: string, link: string): Promise<ContentFrame | null> {
  const contentBase = await contentBaseSettled(qc)
  const appOrigin = window.location.origin
  if (!artifactsHostOf(contentBase, appOrigin)) return null
  const attrs = contentFrameAttrs('own', frameShell())
  try {
    const { frame_url } = await requestArtifactTicket(hash)
    const gate = contentFrameGate({ frameUrl: frame_url, hash, contentBase, appOrigin })
    if (gate.ok) return { src: gate.src, ...attrs }
  } catch {
    // No ticket: the share link, below.
  }
  return { src: link, ...attrs }
}

/**
 * Have artifact pages left this origin for a host of their own? For the boot-time sweep
 * (retireWorkers.ts), which runs with no More panel behind it — so this one ASKS, through
 * the same cached query, and the panel finds the answer already there.
 */
export async function artifactsHaveMoved(qc: QueryClient): Promise<boolean> {
  try {
    await qc.fetchQuery(WHERE)
  } catch {
    return false
  }
  return artifactsHostOf(contentBaseNow(qc), window.location.origin) !== null
}
